import {useCallback, useEffect, useState} from "react";
import {useSingleListing} from "../components/context/SingleListingProvider.tsx";
import {empty} from "../utils/utils.ts";
import useCtrInscription from "./useCtrInscription.tsx";
import useCtrBrokers from "./useCtrBrokers.ts";
import {WebsiteConfig} from "../graphql/generated/graphql-main.tsx";

const clsPopup = "popmake-overlay";
const clsForm = "form.contact-form";
const clsBrokerEmailInput = ".gfield.fillin__agent-email input";
const clsPropAddressInput = ".gfield.fillin__property-address input";

/**
 * Dynamically populate Gravity Form fields with main broker's email & full address (for use in sending leads).
 * Note: to ensure proper synchronicity of datasets and prevent missing parts of the address or broker infos,
 *  I ended up storing the <input> fields in local state and then using an effect to update the input.value
 *  using latest available infos from region (address) and offices (broker) datasets. This works great!
 *
 * @param $config {WebsiteConfig}
 */
export default function useGfContactForm($config?: WebsiteConfig | null) {
    const {listing} = useSingleListing();
    const {displayAddress} = useCtrInscription();
    const {membreByCode, bureauByCode} = useCtrBrokers();

    const [observer, setObserver] = useState<MutationObserver | null>(null);
    const [inputEmail, setInputEmail] = useState<HTMLInputElement | null>(null);
    const [inputAddress, setInputAddress] = useState<HTMLInputElement | null>(null);

    // GENERIC: get main broker's email or that of his/her office (also check if 'restricted office' and do NOT set email then)
    const mainBrokerEmail = () => {
        // do NOT return main broker email if it's from restricted office (lead is sent ONLY to Patrice)
        if ($config == null || $config?.restricted_offices?.includes(listing?.BUREAU_INSCRIPTEUR_1!)) {
            console.log(`useGfContactForm::restricted-office -> ${listing?.NO_INSCRIPTION}.${listing?.BUREAU_INSCRIPTEUR_1}`);
            return "";
        }
        const _broker = membreByCode(listing?.COURTIER_INSCRIPTEUR_1);
        const _office = bureauByCode(_broker?.BUR_CODE);
        return _broker?.COURRIEL ?? _office?.COURRIEL ?? "";
    }
    // GENERIC: get full address of the listing
    const propertyAddress = () => {
        return displayAddress(listing);
    }

    // GENERIC: given HTML form element, extract its email & address fields and make ready for dynamic fill later on)
    const getFormFields = useCallback((form: HTMLFormElement | null) => {
        if (form == null) return null;

        // if form is found, try to get specific fields in that form (we'll populate them dynamically)
        const fldEmail: HTMLInputElement | null = form?.querySelector(clsBrokerEmailInput);
        setInputEmail(fldEmail);
        const fldAddress: HTMLInputElement | null = form?.querySelector(clsPropAddressInput);
        setInputAddress(fldAddress);
    }, []);

    // ELEMENTOR: when mutations happen, filter out irrelevant ones, and only focus on popup gform & its fields
    const onMutations = useCallback((mutations: MutationRecord[]) => {
        // when nodes are removed, ignore (e.g. when popup is closed)
        const _mutations = mutations.filter(it => empty(it.removedNodes));
        // flat-map added nodes per mutation into a single 1-dim array
        const _nodes = _mutations.flatMap(it => [...it.addedNodes]);
        // try to find Elementor popup among the added nodes
        const _popup = (_nodes.find(it => (it as HTMLElement)
            ?.classList?.contains(clsPopup)) ?? null) as HTMLElement | null;
        // if popup is found, try to get GForm <form> node among its children
        const _form: HTMLFormElement | null = _popup?.querySelector(clsForm) ?? null;
        if (_form == null) return null;

        console.log(`popup.gform -> found`, {popup: _popup, form: _form});
        getFormFields(_form);
    }, [getFormFields]);

    // ELEMENTOR: instantiate the observer separately (not subject to change)
    useEffect(() => {
        const obs = new MutationObserver(onMutations);
        setObserver(obs);
    }, [setObserver]);

    // ELEMENTOR: handle hooking / un-hooking the observer to the DOM node
    useEffect(() => {
        if (!observer) return;
        // observe entire <body> since Elementor form popup is created at the root of <body>
        observer.observe(document.body, {childList: true, subtree: true});
        return () => observer?.disconnect();
    }, [observer]);

    // POPUP-MAKER: get Gravity Form generated by PopupMaker and loads its input fields
    useEffect(() => {
        const _form = document.querySelector<HTMLFormElement>(clsForm) ?? null;
        getFormFields(_form);
    }, [getFormFields]);

    // GENERIC: update GForm fields for broker's email & property address - note: that's the only working solution
    useEffect(() => {
        if (inputEmail != null) inputEmail.value = mainBrokerEmail();
        if (inputAddress != null) inputAddress.value = propertyAddress();
    }, [inputEmail, inputAddress, mainBrokerEmail, propertyAddress]);
}